import { PlanConsistencyStatus } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import { useTranslation } from 'react-i18next'

import { Flex } from '@enechain/ecloud-designsystem'
import {
  FaCircleExclamationSolid,
  FaTriangleExclamationSolid,
} from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'

/**
 * 整合ステータス → 表示仕様 (需要調達・発電販売計画一覧。spec `plan-common.md`
 * §一覧テーブルの「整合ステータス」列 / AC-PPL-007b / AC-PGL-006b)。
 *
 * アイコン + 文字で表す (DS Badge はやめた)。写像は業務的意味 → tone:
 *   - 取引相手間ミスマッチ → negative: 丸の ! (Text.negativeMid)。他社との不一致で
 *     GC を越えるとインバランスに直結する重い方
 *   - 計画間不整合・計画内不整合 → warning: 三角の ! (Object.warningMid)。自社で解消する軽い方
 *   - 整合・不整合解消済み・未評価 → none: アイコンなし、文字は Text.lowEmphasis
 *   - UNSPECIFIED → 未評価と同じ表示 (古い backend と組み合わさっても「整合」に見せない)
 */
export type ConsistencyTone = 'negative' | 'warning' | 'none'

export type ConsistencySpec = {
  tone: ConsistencyTone
  /** 表示ラベルの i18n key。 */
  labelKey: string
}

export const PLAN_CONSISTENCY_STATUS_SPEC: Record<
  PlanConsistencyStatus,
  ConsistencySpec
> = {
  [PlanConsistencyStatus.UNSPECIFIED]: {
    tone: 'none',
    labelKey: 'page:planSubmission.consistencyStatus.notEvaluated',
  },
  [PlanConsistencyStatus.CONSISTENT]: {
    tone: 'none',
    labelKey: 'page:planSubmission.consistencyStatus.consistent',
  },
  [PlanConsistencyStatus.MISMATCH_RESOLVED]: {
    tone: 'none',
    labelKey: 'page:planSubmission.consistencyStatus.mismatchResolved',
  },
  [PlanConsistencyStatus.COUNTERPARTY_MISMATCH]: {
    tone: 'negative',
    labelKey: 'page:planSubmission.consistencyStatus.counterpartyMismatch',
  },
  [PlanConsistencyStatus.CROSS_PLAN_INCONSISTENCY]: {
    tone: 'warning',
    labelKey: 'page:planSubmission.consistencyStatus.crossPlanInconsistency',
  },
  [PlanConsistencyStatus.INTERNAL_PLAN_INCONSISTENCY]: {
    tone: 'warning',
    labelKey: 'page:planSubmission.consistencyStatus.internalPlanInconsistency',
  },
  [PlanConsistencyStatus.NOT_EVALUATED]: {
    tone: 'none',
    labelKey: 'page:planSubmission.consistencyStatus.notEvaluated',
  },
}

/**
 * 計画一覧の整合ステータス表示。需要調達・発電販売計画一覧のテーブルで共用する。
 * 想定外の値 (= map に無い数値) は UNSPECIFIED (= 未評価) に fallback する。
 */
export const PlanConsistencyStatusBadge = ({
  status,
}: {
  status: PlanConsistencyStatus
}): React.ReactElement => {
  const { t } = useTranslation()
  const spec =
    PLAN_CONSISTENCY_STATUS_SPEC[status] ??
    PLAN_CONSISTENCY_STATUS_SPEC[PlanConsistencyStatus.UNSPECIFIED]
  return (
    <Flex
      as="span"
      alignItems="center"
      justifyContent="center"
      gap="4px"
      color={spec.tone === 'none' ? 'Text.lowEmphasis' : undefined}
    >
      {spec.tone === 'negative' && (
        <FaCircleExclamationSolid
          boxSize={iconSize.badgeInline}
          color="Text.negativeMid"
          aria-hidden="true"
        />
      )}
      {spec.tone === 'warning' && (
        <FaTriangleExclamationSolid
          boxSize={iconSize.badgeInline}
          color="Object.warningMid"
          aria-hidden="true"
        />
      )}
      {t(spec.labelKey)}
    </Flex>
  )
}
